/**
 * useCoreDepositFeed -- React Query wrappers over four read operations of
 * the core deposit feed (GH #2911, LOI section 2.2). The FI console had no
 * caller for any of them; every delivery, reconciliation report and provenance
 * attestation tempo-engine recorded was readable only through the API.
 *
 * Server contract (core-deposit-feed.openapi.yaml, via coreDepositFeedClient):
 *
 *   GET /api/v1/core-deposit-feed/deliveries                               listFeedDeliveries
 *   GET /api/v1/core-deposit-feed/deliveries/{deliveryId}                  getFeedDelivery
 *   GET /api/v1/core-deposit-feed/deliveries/{deliveryId}/records          listDeliveryRecords
 *   GET /api/v1/core-deposit-feed/provenance-attestation                   getProvenanceAttestation
 *
 * Every read is open to the router's FEED_READ_ROLES (fi_admin,
 * fi_loan_officer, fi_compliance, fi_ops and the platform-admin roles;
 * services/tempo-engine/src/ingestion/routes.ts). The router is mounted behind
 * requireOverdraftPreviewEnabled, the same Institution.enableOverdraftPreview
 * gate as /api/v1/tempo, which answers 404 when the flag is off.
 *
 * 404 RESOLVES TO NULL on the single-resource reads, the useTempoAccount
 * convention: the flag-off stealth 404 and an unknown delivery id return one
 * status, and the contract does not distinguish them, so neither does this
 * hook. The list reads
 * resolve a 404 to an empty page marked `surfaceDark`, so the page can say the
 * surface answered nothing rather than stating that no delivery exists.
 *
 * EVERY OTHER NON-2XX THROWS. A 404 is a recorded absence; a 500 is an unknown
 * state. A caller that reads `data === null` without reading `error` states an
 * absence the server never reported.
 *
 * Cache keys carry the useTempoAccount tenant namespace: 'apex' is a
 * client-side label for platform-admin sessions with no tenantId claim; the
 * backend resolves the tenant from the JWT.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { components, operations } from '@aaimplatform/institutional-types/generated/core-deposit-feed';
import { coreDepositFeedClient } from '../api/client';
import { useAuthStore } from '../stores/auth';

export type FeedDelivery = components['schemas']['FeedDelivery'];
export type DeliveryStatus = components['schemas']['DeliveryStatus'];
export type ReconciliationResult = components['schemas']['ReconciliationResult'];
export type RecordTypeReconciliation = components['schemas']['RecordTypeReconciliation'];
export type ReconciliationDiscrepancy = components['schemas']['ReconciliationDiscrepancy'];
export type AmountTotal = components['schemas']['AmountTotal'];
export type LandedRecord = components['schemas']['LandedRecord'];
export type CoreFeedRecordType = components['schemas']['CoreFeedRecordType'];
export type ProvenanceAttestation = components['schemas']['ProvenanceAttestation'];
export type FeedDeliveryFilters = NonNullable<operations['listFeedDeliveries']['parameters']['query']>;

const DELIVERIES_KEY = 'core-feed-deliveries';
const LAST_ACCEPTED_KEY = 'core-feed-last-accepted';
const DELIVERY_KEY = 'core-feed-delivery';
const RECORDS_KEY = 'core-feed-records';
const ATTESTATION_KEY = 'core-feed-attestation';

/** Page size for the two cursor-paged lists. The contract allows 1-200. */
export const FEED_PAGE_SIZE = 50;

/**
 * A generated page schema plus whether the surface answered 404. The page
 * shape is the contract's; `surfaceDark` is the only field this hook adds.
 */
export type FeedDeliveryPageRead = components['schemas']['FeedDeliveryPage'] & { surfaceDark: boolean };
export type LandedRecordPageRead = components['schemas']['LandedRecordPage'] & { surfaceDark: boolean };

/** Normalize an openapi-fetch error body into an Error carrying the HTTP status. */
function toApiError(error: unknown, response: Response): Error & { status?: number; code?: string } {
  const envelope = error as
    | { error?: { code?: string; message?: string } | string; message?: string }
    | undefined;
  let message = `HTTP ${response.status}`;
  let code: string | undefined;
  if (envelope && typeof envelope.error === 'object' && envelope.error) {
    message = envelope.error.message ?? message;
    code = envelope.error.code;
  } else if (envelope && typeof envelope.message === 'string') {
    message = envelope.message;
  }
  return Object.assign(new Error(message), { status: response.status, code });
}

function useTenantCacheKey(): string {
  const user = useAuthStore((s) => s.user);
  return user?.tenantId ?? 'apex';
}

/** Deliveries, newest received first, filtered as the contract allows. */
export function useFeedDeliveries(filters: Omit<FeedDeliveryFilters, 'cursor' | 'limit'>) {
  const tenantKey = useTenantCacheKey();
  return useInfiniteQuery<FeedDeliveryPageRead, Error>({
    queryKey: [DELIVERIES_KEY, tenantKey, filters],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error, response } = await coreDepositFeedClient.GET(
        '/api/v1/core-deposit-feed/deliveries',
        {
          params: {
            query: {
              ...filters,
              limit: FEED_PAGE_SIZE,
              ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}),
            },
          },
        },
      );
      if (response.status === 404) return { items: [], nextCursor: null, surfaceDark: true };
      if (error || !data) throw toApiError(error, response);
      return { ...data, surfaceDark: false };
    },
    retry: false,
  });
}

/**
 * The most recently received ACCEPTED delivery, the source of the continuity
 * signal. The list is ordered by receivedAt descending, so limit 1 is the
 * latest. Null on a 404 and on an empty list; `surfaceDark` separates them.
 */
export function useLastAcceptedDelivery() {
  const tenantKey = useTenantCacheKey();
  return useQuery<{ delivery: FeedDelivery | null; surfaceDark: boolean }, Error>({
    queryKey: [LAST_ACCEPTED_KEY, tenantKey],
    queryFn: async () => {
      const { data, error, response } = await coreDepositFeedClient.GET(
        '/api/v1/core-deposit-feed/deliveries',
        { params: { query: { status: 'ACCEPTED', limit: 1 } } },
      );
      if (response.status === 404) return { delivery: null, surfaceDark: true };
      if (error || !data) throw toApiError(error, response);
      return { delivery: data.items[0] ?? null, surfaceDark: false };
    },
    retry: false,
  });
}

/**
 * One delivery with its manifest and, once sealed, its reconciliation.
 * `FeedDelivery.reconciliation` carries the same ReconciliationResult the
 * getDeliveryReconciliation operation returns, so the detail screen reads it
 * here rather than spending a second request on it.
 */
export function useFeedDelivery(deliveryId: string | undefined) {
  const tenantKey = useTenantCacheKey();
  return useQuery<FeedDelivery | null, Error>({
    queryKey: [DELIVERY_KEY, tenantKey, deliveryId ?? ''],
    enabled: Boolean(deliveryId),
    queryFn: async () => {
      const { data, error, response } = await coreDepositFeedClient.GET(
        '/api/v1/core-deposit-feed/deliveries/{deliveryId}',
        { params: { path: { deliveryId: deliveryId as string } } },
      );
      if (response.status === 404) return null;
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    retry: false,
  });
}

/** Landed records of one delivery, each with its provenance stamp. */
export function useDeliveryRecords(deliveryId: string | undefined, recordType: CoreFeedRecordType | undefined) {
  const tenantKey = useTenantCacheKey();
  return useInfiniteQuery<LandedRecordPageRead, Error>({
    queryKey: [RECORDS_KEY, tenantKey, deliveryId ?? '', recordType ?? ''],
    enabled: Boolean(deliveryId),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error, response } = await coreDepositFeedClient.GET(
        '/api/v1/core-deposit-feed/deliveries/{deliveryId}/records',
        {
          params: {
            path: { deliveryId: deliveryId as string },
            query: {
              limit: FEED_PAGE_SIZE,
              ...(recordType ? { recordType } : {}),
              ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}),
            },
          },
        },
      );
      if (response.status === 404) return { items: [], nextCursor: null, surfaceDark: true };
      if (error || !data) throw toApiError(error, response);
      return { ...data, surfaceDark: false };
    },
    retry: false,
  });
}

/**
 * The provenance attestation over a business-date window. Both bounds are
 * required by the operation (400 WINDOW_REQUIRED without them), so the query
 * waits until both are set.
 */
export function useProvenanceAttestation(windowFrom: string, windowTo: string) {
  const tenantKey = useTenantCacheKey();
  return useQuery<ProvenanceAttestation | null, Error>({
    queryKey: [ATTESTATION_KEY, tenantKey, windowFrom, windowTo],
    enabled: Boolean(windowFrom && windowTo),
    queryFn: async () => {
      const { data, error, response } = await coreDepositFeedClient.GET(
        '/api/v1/core-deposit-feed/provenance-attestation',
        { params: { query: { windowFrom, windowTo } } },
      );
      if (response.status === 404) return null;
      if (error || !data) throw toApiError(error, response);
      return data;
    },
    retry: false,
  });
}
